/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { API_BASE, SHIFTIQ_API, getAuthHeaders, isAbortError } from "../../api/client";
import { usePgStores } from "../../stores/StoresProvider";
import { useStaff } from "../../hooks";
import { Icon, LoadingPulse } from "../../ui";
import { GROUPS, schedulingGroupFor } from "../../lib/roles";
import { findStaffForRotaRow, rosterScheduleKey } from "../../employeeMatch";
import { employeeMatchesManagerStore } from "../../lib/employeeStore";

import { SPECIAL_STATUSES, specialStatusDisplay, resolveEmployeeName } from "../schedule/scheduleHelpers";
import { scheduleBuilderTableColGroup } from "../schedule/scheduleTable";
import { FoxlineSlotSyncPanel } from "./FoxlineSlotSyncPanel";
import { ErganiXmlPanel } from "./ErganiXmlPanel";
import { canAccessHrTools, canAccessOpsModule, canPushFoxline, isEmployeeRole } from "../../identity/access";
import { useFoxlineSlotSync } from "../../hooks";
import { allowedStoresForUser, isSingleStoreLocked, locationIdForStore, locationScopeQuery } from "../../lib/storeScope";
import { isOwnScheduleChange, isPendingReviewStatus } from "../../lib/scheduleUnseenChanges";
import { WeekPicker } from "../schedule/ScheduleBuilderPage";
import { weekOffsetForMondayISO } from "../../lib/dates";
import { storeParamFromSearch, searchForHrDashboard, hrTabFromSearch, replaceBrowserPath } from "../../routing/paths";
import { resolveSharedStoreName, rememberStoreByName } from "../../lib/selectedStore";
import { defaultBuilderStore } from "../../theme";
import { ladderAllowsSoftonePush, ladderStatusLabel } from "../../lib/scheduleLadder";

export function HRDashboardPage({ user, lang }) {
  const { storeNames, storeRecords, loaded: storesLoaded } = usePgStores();
  const mountSearch = useRef(
    typeof window !== "undefined" ? window.location.search : ""
  ).current;
  const initialHrQuery = useRef(storeParamFromSearch(mountSearch)).current;
  const [weekOffset, setWeekOffset] = useState(() => {
    const fromUrl = weekOffsetForMondayISO(initialHrQuery.week);
    return fromUrl == null ? 0 : fromUrl;
  });
  const [selectedStore, setSelectedStore] = useState(() => {
    if (isSingleStoreLocked(user)) return allowedStoresForUser(user)[0];
    if (isEmployeeRole(user) && user?.store && user.store !== "All Stores") return user.store;
    const shared = resolveSharedStoreName({
      search: typeof window !== "undefined" ? window.location.search : "",
      storeRecords,
      storeNames,
    });
    if (shared) return shared;
    return defaultBuilderStore(storeNames, storeRecords) || user.store || "";
  });
  const selectedLocationId = locationIdForStore(selectedStore, storeRecords);
  const { staff: liveStaff } = useStaff(selectedStore, { unifiedLocationId: selectedLocationId });

  const appliedSharedStore = useRef(false);
  useEffect(() => {
    if (!storesLoaded) return;
    const allowed = allowedStoresForUser(user);
    if (Array.isArray(allowed) && allowed.length === 1) {
      setSelectedStore(allowed[0]);
      return;
    }
    if (Array.isArray(allowed) && allowed.length > 1) {
      setSelectedStore((prev) => (allowed.includes(prev) ? prev : allowed[0]));
      return;
    }
    if (storeNames.length === 1) {
      setSelectedStore(storeNames[0]);
      return;
    }
    if (appliedSharedStore.current || !storeNames.length) return;
    appliedSharedStore.current = true;
    const preferred = resolveSharedStoreName({
      search: typeof window !== "undefined" ? window.location.search : "",
      storeRecords,
      storeNames,
    }) || defaultBuilderStore(storeNames, storeRecords);
    if (preferred) setSelectedStore(preferred);
  }, [storesLoaded, user?.role, user?.store, storeNames, storeRecords]);
  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = hrTabFromSearch(typeof window !== "undefined" ? window.location.search : "");
    if (fromUrl) return fromUrl;
    return canPushFoxline(user) ? "foxline" : "changes";
  });
  const [changeRows, setChangeRows] = useState([]);
  const [changesLoading, setChangesLoading] = useState(false);
  const [weekQueue, setWeekQueue] = useState([]);
  const [queueBusy, setQueueBusy] = useState(false);
  const [foxlineLadderStatus, setFoxlineLadderStatus] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [topbarEl, setTopbarEl] = useState(null);
  const alertsRef = useRef(null);
  const [scheduleByWeek, setScheduleByWeek] = useState({});
  const [schedLoading, setSchedLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [pendingLoans, setPendingLoans] = useState([]);
  const [pendingLoansLoading, setPendingLoansLoading] = useState(false);
  const [loanActionId, setLoanActionId] = useState(null);
  const [selectedChangeIds, setSelectedChangeIds] = useState([]);
  const [ackBusy, setAckBusy] = useState(false);
  const isAdmin = user.role === "admin";

  const dayNames = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const dayNamesGr = ["Δευ","Τρί","Τετ","Πέμ","Παρ","Σάβ","Κυρ"];
  const fullDayGr = { mon: "Δευτέρα", tue: "Τρίτη", wed: "Τετάρτη", thu: "Πέμπτη", fri: "Παρασκευή", sat: "Σάββατο", sun: "Κυριακή" };
  const fullDayEn = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };

  function getMonday(offset) {
    const base = new Date();
    const day = base.getDay();
    const monday = new Date(base);
    monday.setDate(base.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
    monday.setHours(0,0,0,0);
    return monday;
  }
  function toLocalISO(d) {
    const yr  = d.getFullYear();
    const mo  = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${yr}-${mo}-${day}`;
  }
  function fmtDate(d) {
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  }
  function dayKey(d, i) {
    return dayNames[i] + " " + fmtDate(d);
  }
  function formatTimeHHMM(v) {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  function formatDateTime(v) {
    if (!v) return "—";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")} ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  }
  function fullDayLabel(dayKeyRaw) {
    const key = String(dayKeyRaw || "").toLowerCase().slice(0, 3);
    return lang === "gr" ? (fullDayGr[key] || "—") : (fullDayEn[key] || "—");
  }
  function describeChange(row) {
    const t = String(row.change_type || "").toLowerCase();
    const oldV = row.old_value || "—";
    const newV = row.new_value || "—";
    if (t === "assigned" || t === "assign") return lang === "gr" ? `Ανατέθηκε ${newV}` : `Assigned ${newV}`;
    if (t === "removed" || t === "remove") return lang === "gr" ? "Αφαιρέθηκε" : "Removed";
    if (t === "modified" || t === "modify") return lang === "gr" ? `Τροποποιήθηκε ${oldV} → ${newV}` : `Modified ${oldV} → ${newV}`;
    if (row.new_value && !row.old_value) return lang === "gr" ? `Ανατέθηκε ${newV}` : `Assigned ${newV}`;
    if (row.old_value && !row.new_value) return lang === "gr" ? "Αφαιρέθηκε" : "Removed";
    return lang === "gr" ? `Τροποποιήθηκε ${oldV} → ${newV}` : `Modified ${oldV} → ${newV}`;
  }

  const storeOptions = (() => {
    const allowed = allowedStoresForUser(user);
    if (Array.isArray(allowed) && allowed.length) return allowed;
    return storeNames;
  })();
  const currentMonday = getMonday(weekOffset);
  const currentWeekStart = toLocalISO(currentMonday);

  useEffect(() => {
    if (!storesLoaded) return undefined;
    const locationId = locationIdForStore(selectedStore, storeRecords);
    replaceBrowserPath("/hr-dashboard", searchForHrDashboard({
      locationId,
      week: currentWeekStart,
      tab: activeTab,
    }));
    return undefined;
  }, [storesLoaded, selectedStore, storeRecords, currentWeekStart, activeTab]);
  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(currentMonday);
    d.setDate(currentMonday.getDate() + i);
    return d;
  });
  const employees = liveStaff ?? [];
  const storeEmployees = employees
    .filter((u) => employeeMatchesManagerStore(u, selectedStore, storeRecords))
    .map((u) => ({ ...u, group: schedulingGroupFor(u) }));

  const fetchPendingLoans = useCallback((signal) => {
    if (!isAdmin) return Promise.resolve([]);
    setPendingLoansLoading(true);
    return fetch(`${SHIFTIQ_API}/loans/pending`, { headers: getAuthHeaders(), signal })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (signal?.aborted) return [];
        const list = Array.isArray(rows) ? rows : [];
        setPendingLoans(list);
        return list;
      })
      .catch((err) => {
        if (signal?.aborted || isAbortError(err)) return [];
        setPendingLoans([]);
        return [];
      })
      .finally(() => {
        if (!signal?.aborted) setPendingLoansLoading(false);
      });
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const controller = new AbortController();
    fetchPendingLoans(controller.signal);
    return () => controller.abort();
  }, [isAdmin, fetchPendingLoans]);

  useEffect(() => {
    if (!selectedStore || !selectedLocationId) return undefined;
    const controller = new AbortController();
    setChangesLoading(true);
    const scope = locationScopeQuery(selectedLocationId);
    fetch(`${API_BASE}/schedule/changes?${scope}&week_start=${encodeURIComponent(currentWeekStart)}`, { headers: getAuthHeaders(), signal: controller.signal })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        if (!controller.signal.aborted) setChangeRows(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setChangeRows([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setChangesLoading(false);
      });
    return () => controller.abort();
  }, [selectedStore, selectedLocationId, currentWeekStart]);

  useEffect(() => {
    setTopbarEl(document.getElementById("app-topbar-end"));
  }, []);

  useEffect(() => {
    if (!alertsOpen) return undefined;
    function onDoc(e) {
      if (alertsRef.current && !alertsRef.current.contains(e.target)) setAlertsOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [alertsOpen]);

  useEffect(() => {
    const controller = new AbortController();
    fetch(
      `${API_BASE}/schedule/weeks/queue?status=${encodeURIComponent("pending_hr,post_pending_hr")}`,
      { headers: getAuthHeaders(), signal: controller.signal }
    )
      .then((r) => (r.ok ? r.json() : { weeks: [] }))
      .then((body) => {
        if (!controller.signal.aborted) setWeekQueue(Array.isArray(body?.weeks) ? body.weeks : []);
      })
      .catch(() => {
        if (!controller.signal.aborted) setWeekQueue([]);
      });
    return () => controller.abort();
  }, [queueBusy]);

  function focusQueueWeek(w) {
    const storeId = String(w.store_id || "").trim();
    if (storeId) {
      setSelectedStore(storeId);
      rememberStoreByName(storeId, storeRecords);
    }
    const offset = weekOffsetForMondayISO(w.week_start);
    if (offset != null) setWeekOffset(offset);
    setActiveTab("foxline");
    setAlertsOpen(false);
  }

  async function markErganiUploaded(storeId, weekStart) {
    const store = storeId || selectedStore;
    const locId = locationIdForStore(store, storeRecords) ?? selectedLocationId;
    const week = weekStart || currentWeekStart;
    setQueueBusy(true);
    try {
      const res = await fetch(`${API_BASE}/schedule/weeks/mark-ergani`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          store_id: store,
          unified_location_id: locId,
          week_start: week,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body?.detail === "string" ? body.detail : "Publish to Crew failed");
      }
      if (locId != null) {
        await fetch(
          `${API_BASE}/schedule/ergani-drift?${locationScopeQuery(locId)}&week_start=${encodeURIComponent(week)}&store_id=${encodeURIComponent(store)}&notify=true`,
          { headers: getAuthHeaders() }
        ).catch(() => {});
      }
      setToast({
        kind: "ok",
        text: lang === "gr" ? "Δημοσιεύτηκε στο προσωπικό — ειδοποιήθηκαν οι εργαζόμενοι" : "Published to Crew — employees notified",
      });
    } catch (err) {
      setToast({ kind: "err", text: err?.message || "Failed" });
    } finally {
      setQueueBusy(false);
    }
  }

  useEffect(() => {
    if (activeTab !== "schedule" || !selectedStore || !selectedLocationId || !liveStaff || liveStaff.length === 0) return undefined;
    const controller = new AbortController();
    setSchedLoading(true);
    const scope = locationScopeQuery(selectedLocationId);
    fetch(`${API_BASE}/schedule?${scope}&week_start=${encodeURIComponent(currentWeekStart)}`, { headers: getAuthHeaders(), signal: controller.signal })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const liveStoreEmps = liveStaff.filter((e) => employeeMatchesManagerStore(e, selectedStore, storeRecords));
        const weekSched = {};
        rows.forEach((row) => {
          const emp = findStaffForRotaRow(liveStoreEmps, row);
          if (!emp) return;
          const d = new Date(row.rota_date + "T12:00:00");
          const names = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
          const dn = names[d.getDay()];
          const dd = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
          const dk = dn + " " + dd;
          let shiftVal = "";
          if (row.status === "leave") shiftVal = "ΑΔΕΙΑ";
          else if (row.status === "sick") shiftVal = "ΑΝΑΡΡΩΤΙΚΗ";
          else if (row.status === "other_store") shiftVal = "OTHER_STORE";
          else if (row.status === "no_work") shiftVal = "NO_WORK";
          else if (row.is_off) shiftVal = "OFF";
          else if (row.shift_start && row.shift_end) shiftVal = row.shift_start.slice(0,5) + "-" + row.shift_end.slice(0,5);
          if (!weekSched[rosterScheduleKey(emp)]) weekSched[rosterScheduleKey(emp)] = {};
          weekSched[rosterScheduleKey(emp)][dk] = shiftVal;
        });
        if (!controller.signal.aborted) {
          setScheduleByWeek((prev) => ({ ...prev, [weekOffset]: weekSched }));
        }
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
      })
      .finally(() => {
        if (!controller.signal.aborted) setSchedLoading(false);
      });
    return () => controller.abort();
  }, [activeTab, selectedStore, selectedLocationId, currentWeekStart, weekOffset, liveStaff, user.role, storeRecords]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const pendingRows = changeRows.filter(
    (r) => isPendingReviewStatus(r.hr_status) && !isOwnScheduleChange(r, user)
  );
  const confirmedRows = changeRows.filter((r) => r.hr_status === "confirmed");
  const pendingIdSet = new Set(pendingRows.map((r) => r.id));
  const selectedPendingIds = selectedChangeIds.filter((id) => pendingIdSet.has(id));
  const allPendingSelected = pendingRows.length > 0 && selectedPendingIds.length === pendingRows.length;
  const progressTotal = changeRows.length;
  const progressDone = confirmedRows.length;
  const progressPct = progressTotal > 0 ? (progressDone / progressTotal) * 100 : 0;

  useEffect(() => {
    const alive = new Set(
      changeRows
        .filter((r) => isPendingReviewStatus(r.hr_status) && !isOwnScheduleChange(r, user))
        .map((r) => r.id)
    );
    setSelectedChangeIds((prev) => prev.filter((id) => alive.has(id)));
  }, [changeRows, user]);
  const grouped = GROUPS.map(g => ({
    ...g,
    members: storeEmployees.filter(u => u.group === g.key),
  })).filter(g => g.members.length > 0);
  const schedule = scheduleByWeek[weekOffset] || {};
  const getCell = (empId, dk) => schedule[empId]?.[dk] || "";
  const SPECIAL = SPECIAL_STATUSES;
  const softoneLadderReady = ladderAllowsSoftonePush(foxlineLadderStatus);
  const foxlineSync = useFoxlineSlotSync({
    store: selectedStore,
    weekStartISO: currentWeekStart,
    weekDates,
    dayKeyFn: dayKey,
    localSchedule: schedule,
    staffPool: storeEmployees,
    storeRecords,
    user,
    enabled: activeTab === "foxline" && Boolean(selectedStore),
    softoneWritesAllowed: softoneLadderReady,
    lang,
  });

  useEffect(() => {
    if (activeTab !== "foxline" || !selectedStore || !selectedLocationId || !currentWeekStart) {
      setFoxlineLadderStatus(null);
      return undefined;
    }
    const controller = new AbortController();
    const scope = locationScopeQuery(selectedLocationId);
    const qs = `${scope}&week_start=${encodeURIComponent(currentWeekStart)}&store_id=${encodeURIComponent(selectedStore)}`;
    fetch(`${API_BASE}/schedule/weeks/approval?${qs}`, {
      headers: getAuthHeaders(),
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (!controller.signal.aborted) {
          setFoxlineLadderStatus(body?.status || "draft");
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setFoxlineLadderStatus("draft");
      });
    return () => controller.abort();
  }, [activeTab, selectedStore, selectedLocationId, currentWeekStart, queueBusy]);

  async function reviewLoanRequest(requestId, action) {
    setLoanActionId(requestId);
    try {
      const res = await fetch(`${SHIFTIQ_API}/loans/${requestId}/${action}`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ reviewed_by: user.name || user.email }),
      });
      if (!res.ok) {
        let detail = lang === "gr" ? "Σφάλμα" : "Request failed";
        try {
          const errBody = await res.json();
          if (typeof errBody?.detail === "string") detail = errBody.detail;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      await fetchPendingLoans();
      setToast({
        kind: "ok",
        text:
          action === "approve"
            ? lang === "gr"
              ? "Ο δανεισμός εγκρίθηκε"
              : "Loan approved"
            : lang === "gr"
              ? "Το αίτημα απορρίφθηκε"
              : "Loan request rejected",
      });
    } catch (err) {
      setToast({
        kind: "err",
        text: err?.message || (lang === "gr" ? "Σφάλμα — δοκιμάστε ξανά" : "Error — please try again"),
      });
    } finally {
      setLoanActionId(null);
    }
  }

  async function refreshChangeRows() {
    const refresh = await fetch(
      `${API_BASE}/schedule/changes?${locationScopeQuery(selectedLocationId)}&week_start=${encodeURIComponent(currentWeekStart)}`,
      { headers: getAuthHeaders() }
    );
    const rows = refresh.ok ? await refresh.json() : [];
    setChangeRows(Array.isArray(rows) ? rows : []);
  }

  async function acknowledgeChanges(ids) {
    const unique = [...new Set((ids || []).filter((id) => id != null))];
    if (!unique.length || ackBusy) return;
    setAckBusy(true);
    try {
      const res = await fetch(`${API_BASE}/schedule/acknowledge-changes`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ change_ids: unique, hr_user_name: user.name }),
      });
      if (!res.ok) throw new Error("bad");
      await res.json();
      setSelectedChangeIds((prev) => prev.filter((id) => !unique.includes(id)));
      await refreshChangeRows();
    } catch {
      setToast({ kind: "err", text: lang === "gr" ? "Σφάλμα — δοκιμάστε ξανά" : "Error — please try again" });
    } finally {
      setAckBusy(false);
    }
  }

  function toggleChangeSelected(id) {
    setSelectedChangeIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  }

  function toggleSelectAllPending() {
    if (allPendingSelected) {
      setSelectedChangeIds([]);
      return;
    }
    setSelectedChangeIds(pendingRows.map((r) => r.id));
  }

  return (
    <div className="anim-fade">
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3000,
            background: toast.kind === "ok" ? "#ECFDF5" : "#FEF0ED",
            color: toast.kind === "ok" ? "#166534" : "var(--danger)",
            border: `1px solid ${toast.kind === "ok" ? "#BBF7D0" : "#FACFC8"}`,
            borderRadius: 10,
            padding: "10px 14px",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {toast.text}
        </div>
      )}
      {topbarEl
        && createPortal(
          <div ref={alertsRef} style={{ position: "relative" }}>
            <button
              type="button"
              className="btn-base btn-outline btn-small"
              aria-expanded={alertsOpen}
              aria-label={lang === "gr" ? "Ειδοποιήσεις" : "Alerts"}
              onClick={() => setAlertsOpen((o) => !o)}
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 12px",
              }}
            >
              <Icon name="megaphone" size={16} />
              <span style={{ fontSize: 12, fontWeight: 800 }}>
                {lang === "gr" ? "Ειδοποιήσεις" : "Alerts"}
              </span>
              {weekQueue.length > 0 ? (
                <span
                  style={{
                    minWidth: 18,
                    height: 18,
                    borderRadius: 999,
                    background: "var(--danger, #B91C1C)",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 800,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 5px",
                  }}
                >
                  {weekQueue.length}
                </span>
              ) : null}
            </button>
            {alertsOpen ? (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 8px)",
                  width: 360,
                  maxWidth: "min(360px, 92vw)",
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                  zIndex: 200,
                  overflow: "hidden",
                }}
              >
                <div style={{ padding: "10px 12px", fontSize: 12, fontWeight: 800, borderBottom: "1px solid var(--border)" }}>
                  {lang === "gr" ? "Εκκρεμή SoftOne / Ergani" : "Pending SoftOne / Ergani"}
                </div>
                {weekQueue.length === 0 ? (
                  <div style={{ padding: 14, fontSize: 12, color: "var(--text-muted)" }}>
                    {lang === "gr" ? "Καμία εκκρεμότητα" : "No pending weeks"}
                  </div>
                ) : (
                  weekQueue.map((w) => (
                    <div
                      key={`${w.store_id}-${w.week_start}`}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        padding: "10px 12px",
                        borderBottom: "1px solid var(--border)",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => focusQueueWeek(w)}
                        style={{
                          border: "none",
                          background: "transparent",
                          textAlign: "left",
                          padding: 0,
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)" }}>
                          {w.store_id}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                          {w.week_start} · {ladderStatusLabel(w.status, lang === "gr" ? "gr" : "en")}
                        </div>
                      </button>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="btn-base btn-outline btn-small"
                          onClick={() => focusQueueWeek(w)}
                        >
                          {lang === "gr" ? "Άνοιγμα καταστήματος" : "Open store"}
                        </button>
                        <button
                          type="button"
                          className="btn-base btn-green btn-small"
                          disabled={queueBusy}
                          onClick={() => {
                            focusQueueWeek(w);
                            void markErganiUploaded(w.store_id, w.week_start);
                          }}
                        >
                          {lang === "gr" ? "Δημοσίευση στο προσωπικό" : "Publish to Crew"}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : null}
          </div>,
          topbarEl
        )}
      <div className="hr-dashboard-toolbar">
        <select
          className="form-select"
          value={selectedStore}
          onChange={(e) => {
            setSelectedStore(e.target.value);
            rememberStoreByName(e.target.value, storeRecords);
          }}
          disabled={storeOptions.length === 1}
          style={{
            width: `calc(${Math.ceil(Math.max(String(selectedStore || "Select store").length, 8) * 1.6)}ch + 40px)`,
            fieldSizing: "content",
            maxWidth: "none",
            height: 34,
            lineHeight: "32px",
            padding: "0 28px 0 12px",
            borderRadius: 10,
            border: "1.5px solid var(--border)",
            background: "var(--surface2)",
            fontSize: 13,
            fontWeight: 800,
            color: "var(--text-primary)",
            opacity: storeOptions.length === 1 ? 0.75 : 1,
            flexShrink: 0,
            boxSizing: "border-box",
          }}
        >
          {!selectedStore ? (
            <option value="">{lang === "gr" ? "Επιλέξτε κατάστημα" : "Select store"}</option>
          ) : null}
          {storeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <WeekPicker
          weekStartISO={currentWeekStart}
          goLiveWeek="2026-05-25"
          lang={lang}
          onCommit={(mondayISO) => {
            const offset = weekOffsetForMondayISO(mondayISO);
            if (offset != null) setWeekOffset(offset);
          }}
        />
        <div style={{ display: "flex", background: "var(--surface2)", borderRadius: 10, padding: 3, gap: 3, marginLeft: "auto", flexShrink: 0 }}>
          <button type="button" onClick={() => setActiveTab("changes")} style={{ background: activeTab === "changes" ? "var(--green)" : "transparent", color: activeTab === "changes" ? "#fff" : "var(--text-secondary)", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer" }}>
            {lang === "gr" ? "Αλλαγές" : "Changes"}
          </button>
          <button type="button" onClick={() => setActiveTab("foxline")} style={{ background: activeTab === "foxline" ? "var(--green)" : "transparent", color: activeTab === "foxline" ? "#fff" : "var(--text-secondary)", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer" }}>
            SoftOne
          </button>
          {canAccessHrTools(user) ? (
            <button type="button" onClick={() => setActiveTab("ergani")} style={{ background: activeTab === "ergani" ? "var(--green)" : "transparent", color: activeTab === "ergani" ? "#fff" : "var(--text-secondary)", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer" }}>
              {lang === "gr" ? "Εργάνη" : "Ergani"}
            </button>
          ) : null}
          {canAccessOpsModule(user, "schedule", "view") ? (
            <button type="button" onClick={() => setActiveTab("schedule")} style={{ background: activeTab === "schedule" ? "var(--green)" : "transparent", color: activeTab === "schedule" ? "#fff" : "var(--text-secondary)", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer" }}>
              {lang === "gr" ? "Πρόγραμμα" : "Schedule"}
            </button>
          ) : null}
        </div>
      </div>
      <div className="hr-dashboard-body">

      {activeTab === "ergani" ? (
        <ErganiXmlPanel
          lang={lang}
          store={selectedStore}
          weekStart={currentWeekStart}
          weekDates={weekDates}
          staff={storeEmployees}
        />
      ) : activeTab === "foxline" ? (
        <FoxlineSlotSyncPanel
          lang={lang}
          user={user}
          sync={foxlineSync}
          softoneWritesAllowed={softoneLadderReady}
          employeeNameFor={(code) => {
            const emp = (liveStaff || []).find((e) => rosterScheduleKey(e) === String(code));
            return emp ? (resolveEmployeeName(emp.name, liveStaff || []) || emp.name) : code;
          }}
        />
      ) : activeTab === "changes" ? (
        <>
        {isAdmin && (
          <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text-primary)", marginBottom: 10 }}>
              {lang === "gr" ? "Αιτήματα δανεισμού" : "Loan Requests"}
            </div>
            {pendingLoansLoading ? (
              <LoadingPulse size={18} />
            ) : pendingLoans.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {lang === "gr" ? "Δεν υπάρχουν εκκρεμή αιτήματα δανεισμού" : "No pending loan requests"}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {pendingLoans.map((loan) => {
                  const busy = loanActionId === loan.id;
                  const loanDateLabel = loan.loan_date
                    ? new Date(`${loan.loan_date}T12:00:00`).toLocaleDateString(
                        lang === "gr" ? "el-GR" : "en-GB",
                        { day: "2-digit", month: "short", year: "numeric" }
                      )
                    : "—";
                  return (
                    <div
                      key={loan.id}
                      style={{
                        border: "1px solid var(--border)",
                        background: "var(--surface2)",
                        borderRadius: 10,
                        padding: "12px 14px",
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)" }}>
                        {loan.employee_name || loan.employee_code}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.45 }}>
                        {lang === "gr" ? "Σπίτι" : "Home"}: {loan.home_store_id || "—"}
                        {" · "}
                        {lang === "gr" ? "Αίτηση" : "Requesting"}: {loan.requesting_store_id || "—"}
                        {" · "}
                        {loanDateLabel}
                      </div>
                      {loan.reason ? (
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>{loan.reason}</div>
                      ) : null}
                      {loan.created_at ? (
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                          {lang === "gr" ? "Υποβλήθηκε" : "Submitted"}: {formatDateTime(loan.created_at)}
                        </div>
                      ) : null}
                      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => reviewLoanRequest(loan.id, "approve")}
                          style={{
                            border: "none",
                            borderRadius: 8,
                            padding: "6px 14px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: busy ? "wait" : "pointer",
                            background: "#0D9488",
                            color: "#fff",
                          }}
                        >
                          {lang === "gr" ? "Έγκριση" : "Approve"}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => reviewLoanRequest(loan.id, "reject")}
                          style={{
                            border: "none",
                            borderRadius: 8,
                            padding: "6px 14px",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: busy ? "wait" : "pointer",
                            background: "#DC2626",
                            color: "#fff",
                          }}
                        >
                          {lang === "gr" ? "Απόρριψη" : "Reject"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", marginBottom: 8 }}>
            {lang === "gr" ? `${progressDone} / ${progressTotal} αλλαγές επιβεβαιωμένες` : `${progressDone} / ${progressTotal} changes confirmed`}
          </div>
          <div style={{ width: "100%", height: 6, borderRadius: 999, background: "var(--surface2)", overflow: "hidden", marginBottom: 14 }}>
            <div style={{ width: `${progressPct}%`, height: "100%", background: "var(--green)" }} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#F59E0B" }}>
              {lang === "gr" ? "Εκκρεμείς" : "Pending"}
            </div>
            {pendingRows.length > 0 ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={allPendingSelected}
                    onChange={toggleSelectAllPending}
                    disabled={ackBusy}
                  />
                  {lang === "gr" ? "Επιλογή όλων" : "Select all"}
                </label>
                <button
                  type="button"
                  disabled={ackBusy || selectedPendingIds.length === 0}
                  onClick={() => acknowledgeChanges(selectedPendingIds)}
                  style={{
                    border: "1.5px solid var(--green)",
                    background: selectedPendingIds.length ? "var(--green)" : "transparent",
                    color: selectedPendingIds.length ? "#fff" : "var(--green)",
                    borderRadius: 8,
                    padding: "6px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: ackBusy || selectedPendingIds.length === 0 ? "not-allowed" : "pointer",
                    opacity: ackBusy || selectedPendingIds.length === 0 ? 0.5 : 1,
                  }}
                >
                  {ackBusy
                    ? (lang === "gr" ? "Αποθήκευση…" : "Saving…")
                    : selectedPendingIds.length
                      ? (lang === "gr" ? `Καταχωρήθηκε στο SOFTONE (${selectedPendingIds.length})` : `Entered in SOFTONE (${selectedPendingIds.length})`)
                      : (lang === "gr" ? "Καταχωρήθηκε στο SOFTONE" : "Entered in SOFTONE")}
                </button>
              </div>
            ) : null}
          </div>
          {changesLoading ? <LoadingPulse size={18} /> : pendingRows.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {lang === "gr" ? "Καμία εκκρεμής αλλαγή" : "No pending changes"}
            </div>
          ) : (
            <div className="card-grid" style={{ gap: 8 }}>
              {pendingRows.map((row) => {
                const checked = selectedPendingIds.includes(row.id);
                return (
                  <div key={row.id} style={{ border: "1px solid var(--border)", background: "var(--surface2)", borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column" }}>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={ackBusy}
                        onChange={() => toggleChangeSelected(row.id)}
                        style={{ marginTop: 2 }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
                          {fullDayLabel(row.day_key)} — {resolveEmployeeName(row.employee_name, liveStaff || []) || "—"}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>{describeChange(row)}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
                          {formatTimeHHMM(row.changed_at)} {lang === "gr" ? `από ${row.changed_by_name || "—"}` : `by ${row.changed_by_name || "—"}`}
                        </div>
                      </span>
                    </label>
                    <button
                      type="button"
                      disabled={ackBusy}
                      onClick={() => acknowledgeChanges([row.id])}
                      style={{
                        marginTop: 8,
                        alignSelf: "flex-start",
                        border: "1.5px solid var(--green)",
                        background: "transparent",
                        color: "var(--green)",
                        borderRadius: 8,
                        padding: "6px 10px",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: ackBusy ? "not-allowed" : "pointer",
                        opacity: ackBusy ? 0.5 : 1,
                      }}
                    >
                      {lang === "gr" ? "Καταχωρήθηκε στο SOFTONE" : "Entered in SOFTONE"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ fontSize: 12, fontWeight: 800, color: "var(--green)", marginTop: 16, marginBottom: 8 }}>
            {lang === "gr" ? "Επιβεβαιωμένες" : "Confirmed"}
          </div>
          {confirmedRows.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {lang === "gr" ? "Καμία επιβεβαιωμένη αλλαγή ακόμα" : "No confirmed changes yet"}
            </div>
          ) : (
            <div className="card-grid" style={{ gap: 8 }}>
              {confirmedRows.map((row) => (
                <div key={row.id} style={{ border: "1px solid var(--border)", background: "var(--surface2)", borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
                    {fullDayLabel(row.day_key)} — {resolveEmployeeName(row.employee_name, liveStaff || []) || "—"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>{describeChange(row)}</div>
                  <div style={{ fontSize: 11, color: "var(--green)", marginTop: 3 }}>
                    {lang === "gr"
                      ? `Επιβεβαιώθηκε από ${row.hr_acknowledged_by || "—"} στις ${formatDateTime(row.hr_acknowledged_at)}`
                      : `Confirmed by ${row.hr_acknowledged_by || "—"} at ${formatDateTime(row.hr_acknowledged_at)}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: 16, border: "1px solid var(--border)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <table style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%", minWidth: 780, background: "var(--surface)", fontSize: 12 }}>
            {scheduleBuilderTableColGroup(weekDates)}
            <thead>
              <tr>
                <th style={{ background: "var(--header-dark)", color: "#fff", padding: "8px 14px", textAlign: "left", fontWeight: 900, fontSize: 12, letterSpacing: 0.5, whiteSpace: "nowrap", width: 160 }}>ONOM/NYMO</th>
                {weekDates.map((d, i) => (
                  <th key={i} style={{ background: "var(--header-dark)", color: "#fff", padding: "6px 8px", textAlign: "center", fontWeight: 700, fontSize: 11, borderLeft: "1px solid rgba(255,255,255,0.1)", minWidth: 110 }}>
                    <div>{lang === "gr" ? dayNamesGr[i] : dayNames[i]}, {fmtDate(d)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped.map(group => (
                <React.Fragment key={group.key}>
                  <tr>
                    <td colSpan={8} style={{ background: "var(--header-dark)", color: "#fff", padding: "7px 14px", fontWeight: 900, fontSize: 11, letterSpacing: 1, textTransform: "uppercase" }}>
                      {lang === "gr" ? group.labelGr : group.label}
                    </td>
                  </tr>
                  {group.members.map(emp => (
                    <tr key={emp.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "8px 14px", fontWeight: 700, color: "var(--text-primary)", background: "var(--surface)", whiteSpace: "nowrap", borderRight: "1px solid var(--border)" }}>
                        <div title={resolveEmployeeName(emp.name, liveStaff || [])} style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{resolveEmployeeName(emp.name, liveStaff || []).toUpperCase()}</div>
                      </td>
                      {weekDates.map((d, i) => {
                        const dk = dayKey(d, i);
                        const val = getCell(rosterScheduleKey(emp), dk);
                        const sp = val ? SPECIAL.find(s => val === s.value) : null;
                        const cellBg = sp ? sp.color : val ? "#ECFDF5" : "var(--surface)";
                        const cellColor = sp ? sp.textColor : val ? "#065F46" : "var(--text-muted)";
                        return (
                          <td key={i} style={{ borderLeft: "1px solid var(--border)", padding: 0, minWidth: 110 }}>
                            <div style={{ minHeight: 48, display: "flex", alignItems: "center", justifyContent: "center", background: cellBg, color: cellColor, fontWeight: val ? 700 : 400, fontSize: 11, userSelect: "none", textAlign: "center", padding: "0 4px" }}>
                              {schedLoading ? <LoadingPulse size={16} color="var(--border)" /> : (!val ? <span style={{ color: "var(--border)", fontSize: 11 }}>–</span> : <span style={{ fontWeight: 700 }}>{sp ? specialStatusDisplay(val, lang) : val}</span>)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {storeEmployees.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>{lang === "gr" ? "Δεν υπάρχουν υπάλληλοι για αυτό το κατάστημα" : "No employees assigned to this store"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
}

